import { Box, Button, Skeleton } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { useEffect, useState } from "react";
import { formatCurrency, getInvoiceStatus } from "../../../../helpers/financialHelpers";
import { postInstallmentPayment } from "../../../../api/invoices";
import InstallmentPaymentModal from "./InstallmentPaymentModal";
import dayjs, { formatDate } from "../../../../helpers/dayjsConfig";
import SuccessAlertSnackbar from "../../../../../components/shared/snackbars/SuccessAlertSnackbar";
import InstallmentShareModal from "./InstallmentShareModal";

const InstallmentsTable = ({ invoice, setLoadingInstallmentsTable, loadingInstallmentsTable }) => {
    const [open, setOpen] = useState(false);
    const [selectedRow, setSelectedRow] = useState(null);
    const [paymentDate, setPaymentDate] = useState(dayjs());
    const [fineAndInterestValue, setFineAndInterestValue] = useState("");
    const [loading, setLoading] = useState(false);
    const [installments, setInstallments] = useState(invoice?.installments || []);
    const [postInstallmentsApiError, setPostInstallmentsApiError] = useState(null);
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState("");

    const handleOpenSnackbar = (message) => {
        setSnackbarMessage(message);
        setSnackbarOpen(true);
    };

    useEffect(() => {
        if (invoice?.installments) {
            setInstallments(invoice.installments);
        }
    }, [invoice?.installments]);

    const columns = [
        { field: 'installmentNumber', headerName: 'Parcela', flex: 1 },
        { field: 'value', headerName: 'Valor', flex: 1 },
        {
            field: 'due_date', headerName: 'Vencimento', flex: 1, type: 'date',
            renderCell: (params) => params?.value ? formatDate(params.value) : "-",
        },
        {
            field: 'status', headerName: 'Status', flex: 1,
            renderCell: (params) =>
                (loading || loadingInstallmentsTable) ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
                        <Skeleton width="7.5rem" height={30} />
                    </Box>
                ) : (
                    params.value
                )
        },
        {
            field: 'paid_value', headerName: 'Valor pago', flex: 1,
            renderCell: (params) =>
                (loading || loadingInstallmentsTable) ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
                        <Skeleton width="5rem" height={30} />
                    </Box>
                ) : (
                    params.value
                )
        },
        {
            field: 'payment_date', headerName: 'Pagamento', flex: 1, type: 'date',
            renderCell: (params) =>
                (loading || loadingInstallmentsTable) ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
                        <Skeleton width="5rem" height={30} />
                    </Box>
                ) : (
                    params.value ? formatDate(params.value) : "-"
                )
        },
        {
            field: 'button',
            headerName: '',
            sortable: false,
            filterable: false,
            disableColumnMenu: true,
            maxWidth: '8.18rem',
            flex: 1.5,
            renderCell: (params) => {
                const handleClick = (e) => e.stopPropagation();
                if ((params.row.originalStatus === "pending" || params.row.originalStatus === "overdue") && invoice?.integrated === true) {
                    return (
                        <Box onClick={handleClick} sx={{ width: '100%' }}>
                            <InstallmentShareModal invoice={invoice}
                                onCopySuccess={handleOpenSnackbar}
                            />
                        </Box>
                    );
                }
                if ((params.row.originalStatus === "pending" || params.row.originalStatus === "overdue") && !loadingInstallmentsTable) {
                    return (
                        <Box onClick={handleClick} sx={{ width: '100%' }}>
                            <Button
                                variant="contained"
                                color="primary"
                                onClick={() => handleOpenPaymentModal(params.row)}
                                sx={{
                                    width: '100%',
                                    fontSize: '0.81rem',
                                    whiteSpace: 'nowrap',
                                }}
                            >
                                Definir como paga
                            </Button>
                        </Box>
                    );
                }
                return null;
            },
        },
    ];

    const rows = installments?.map((installment, index) => ({
        id: installment.id,
        installmentNumber: index + 1,
        value: `R$ ${formatCurrency(installment.value)}`,
        due_date: installment.due_date ? dayjs(installment.due_date).toDate() : null,
        status: getInvoiceStatus(installment.status).label,
        originalStatus: installment.status,
        paid_value: installment.paid_value && Number(installment.paid_value) > 0 ? `R$ ${formatCurrency(installment.paid_value)}` : "-",
        payment_date: installment.payment_date ? dayjs(installment.payment_date).toDate() : null,
    })) || [];

    const handleOpenPaymentModal = (row) => {
        setSelectedRow(row);
        setOpen(true);
    };
    const handleClosePaymentModal = () => {
        setOpen(false);
        setSelectedRow(null);
        setPaymentDate(dayjs());
        setFineAndInterestValue("");
        setPostInstallmentsApiError(null);
    };

    const handleConfirmPayment = async () => {
        if (!selectedRow || !paymentDate) return;
        setLoading(true);
        setLoadingInstallmentsTable(true);

        const body = {
            installment_id: selectedRow.id,
            payment_date: paymentDate,
            interest_paid_value: fineAndInterestValue || 0,
        };
        try {
            const updatedInvoice = await postInstallmentPayment({
                invoiceId: invoice.id,
                body,
            });

            setInstallments(updatedInvoice.installments);
            handleClosePaymentModal();

            handleOpenSnackbar("Parcela paga com sucesso!");
        } catch (error) {
            setPostInstallmentsApiError(error.message);
        } finally {
            setLoadingInstallmentsTable(false);
            setLoading(false);
        }
    };

    return (
        <Box>
            <DataGrid
                rows={rows}
                columns={columns}
                hideFooter
                disableColumnMenu
                disableSelectionOnClick
                loading={!invoice}
                sx={{
                    border: 'none',
                    '& .MuiDataGrid-columnHeader': {
                        px: '2rem',
                    },
                    '& .MuiDataGrid-cell': {
                        borderRight: 'none',
                        px: '2rem',
                    },
                    '& .MuiDataGrid-columnSeparator': {
                        display: 'none',
                    },
                    '& .MuiDataGrid-row': {
                        borderBottom: '1px solid theme.palette.divider',
                    },

                    '.MuiDataGrid-columnHeaderTitle': {
                        fontWeight: 'bold',
                    },
                }}
            />
            <InstallmentPaymentModal
                open={open}
                onClose={handleClosePaymentModal}
                selectedRow={selectedRow}
                paymentDate={paymentDate}
                setPaymentDate={setPaymentDate}
                fineAndInterestValue={fineAndInterestValue}
                setFineAndInterestValue={setFineAndInterestValue}
                onConfirm={handleConfirmPayment}
                loading={loading}
                postInstallmentsApiError={postInstallmentsApiError}
                setPostInstallmentsApiError={setPostInstallmentsApiError}
            />
            <SuccessAlertSnackbar
                open={snackbarOpen}
                onClose={() => setSnackbarOpen(false)}
                message={snackbarMessage}
                vertical="bottom"
                horizontal="center"
            />
        </Box>
    )
}

export default InstallmentsTable;